/**
 * Created by rathompson on 12/6/21
 * File: AssignRoles
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  ModalBody,
  Title,
  Body,
  ModalFooter,
  Button, Bold,
} from '@panwds/react-ui'
import {
  SubmitButton,
} from '@panwds/react-form'
import AssignRolesForm from '../../../common/AssignRolesForm/AssignRolesForm'
import {
  LABEL_ASSIGN_ROLES,
  LABEL_OPTIONAL,
  BUTTON_BACK,
  BUTTON_REMOVE, TOOLTIP_READ_ONLY,
} from '../../../../constants/LabelConstants'
import {
  DATA_TEST_ADD_IDENTITY_CANCEL,
  DATA_TEST_ADD_IDENTITY_SUBMIT,
} from '../../../../../common/constants/TestingDataIds'
import { IDENTITIES_PERMISSION_SET, IDENTITY_TYPES } from '../../../../constants/IAMConstants'
import IAMService from '../../../../services/IAMService'
import { prn } from '../../../../utils/prn'
import { ARRAY, OBJECT } from '../../../../../common/utils/Utils'
import { useFormContext } from 'react-hook-form'
import AuthContext from '../../../../../common/context/AuthContext'
import { revokeTsgToken } from '@sparky/framework'
import { getVarValue } from '../../../../../common/utils/Sparky'
import { FF_ALLOW_BROWSER_ROLE } from '../../../../../common/utils/FeatureFlags'
import GlobalContext from '../../../../../common/context/GlobalContext'
import _ from 'lodash'
import useReadOnly from '../../../../../common/hooks/useReadOnly'
import TooltipButton from '../../../../../common/components/tooltipButton/TooltipButton'

const allowBrowserRole = getVarValue(FF_ALLOW_BROWSER_ROLE, false)

const Footer = ({ isEdit, onRemove, isLoading, member, tenant, onBackPressed, optional, length }) => {
  const { formState } = useFormContext()
  const { isValid } = formState
  const { treeReducer } = useContext(GlobalContext)
  const { selectedTreeNode = {} } = treeReducer
  const readOnly = useReadOnly(selectedTreeNode?.id, IDENTITIES_PERMISSION_SET)

  return (
    <ModalFooter style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '43px' }}>
      {isEdit && (readOnly ? (
        <TooltipButton
          label={ TOOLTIP_READ_ONLY }
          disabled
          tooltipProps={{ defaultPlacement: 'bottom-end' }}
          buttonProps = {{ appearance: 'secondary-destructive' }}>
          {BUTTON_REMOVE}
        </TooltipButton>
      ) : (
        <Button
          appearance="secondary-destructive"
          onClick={onRemove}
          disabled={readOnly || isLoading || member?.inherited_from?.some(id => id !== tenant?.id) || length <= 1}
        >{BUTTON_REMOVE}</Button>
      ))}
      <Button
        data-testid={DATA_TEST_ADD_IDENTITY_CANCEL}
        onClick={onBackPressed}
        disabled={isLoading}
        appearance="secondary"
      >{BUTTON_BACK}</Button>
      <SubmitButton
        data-testid={DATA_TEST_ADD_IDENTITY_SUBMIT}
        appearance="primary"
        disabled={isLoading || (!isValid && !optional)}
      />
    </ModalFooter>
  )
}

/**
 *   Role assignment tab
 */
const AssignRoles = ({
  isEdit,
  onRemove,
  isLoading,
  member,
  tenant,
  onBackPressed,
  onNextPressed,
  setLoading,
  identityType,
  error: _error,
  length,
}) => {

  const { user } = useContext(AuthContext)
  const { iamReducer = {} } = useContext(GlobalContext)
  const { policiesMap = {} } = iamReducer

  const [error, setError] = useState(_error)

  const optional = identityType === IDENTITY_TYPES.SERVICE_ACCOUNT

  useEffect(() => {
    if (_error && _error !== error) {
      setError(_error)
    }
  }, [_error, error])

  const userPolicies = useMemo(() => ARRAY.clean(policiesMap?.[member?.identity_email]), [policiesMap, member?.identity_email])

  /**
   *     ========= Callback ===========
   */
  const onSubmit = useCallback(async (body) => {
    setLoading(true)
    try {
      delete body?.['undefined']
      const keys = Object.keys(body)
      for (const key of keys) {
        if (key.includes('-disabled') || !ARRAY.hasArray(body[key])) {
          delete body[key]
        }
      }

      const principal = member.identity_email

      const currentTSGPolicies = userPolicies.filter(policy => policy.resource.tsg_id === tenant?.id)

      const payloads = []
      const policiesToDelete = []
      let customRolesAdded = false

      const roles_to_add = _.flattenDeep(Object.entries(body).map(([appId, roles]) => {
        const app_id = appId === 'all' ? '' : appId

        return roles.map(role => {
          if (role.id?.includes(':')) {
            customRolesAdded = true
          }
          const resource = prn.format({
            protocol: 'prn',
            tsg_id: tenant?.id,
            app_id,
            region_id: '',
            instance_id: '',
            resource_scope: '',
          })
          return {
            role,
            app_id,
            tsg_id: tenant?.id,
            payload: {
              principal,
              role: role.id || role.name,
              resource
            }
          }
        })
      }))

      if (isEdit && OBJECT.hasObject(member)) {
        policiesToDelete.push(...currentTSGPolicies.filter(policy => {
          return !roles_to_add.find(({ role, app_id }) => role.name === policy.role && app_id === policy.resource.app_id && role.resource_scope === policy.resource.resource_scope)
        }))
      }
      payloads.push(...roles_to_add.filter(({ role, app_id, tsg_id }) => {
        return !currentTSGPolicies.some(policy => policy.role === role.name && policy.resource.tsg_id === tsg_id && policy.resource.app_id === app_id)
      }).map(({ payload }) => payload))

      const isSelfDelete = policiesToDelete.some(policy => policy.principal === user?.email)

      for (const payload of payloads) {
        await IAMService.accessPolicies.create(tenant?.id, payload)
      }
      for (const policy of policiesToDelete) {
        const policy_id = policy.id
        await IAMService.accessPolicies.delete(tenant?.id, policy_id)
      }

      if (!optional && allowBrowserRole && (!body['all'] || customRolesAdded)) {
        await IAMService.accessPolicies.create(tenant?.id, {
          role: 'browser',
          principal: member.identity_email,
          resource: prn.format({
            protocol: 'prn',
            tsg_id: tenant?.id,
            app_id: '',
            region_id: '',
            instance_id: '',
            resource_scope: '',
          }),
        })
      }
      onNextPressed(true, member)
      if (isSelfDelete && tenant?.id) {
        await revokeTsgToken(tenant?.id)
      }
    }
    catch (error) {
      setError(error.message)
    }
    setLoading(false)
  }, [setLoading, member, userPolicies, isEdit, optional, onNextPressed, tenant?.id, user?.email])

  return (
    <div className="tw-h-full tw-relative">
      <ModalBody addClassName='tw-overflow-y-scroll' style={{ height: 'calc(100% - 43px)' }}>
        <div className="tw-flex tw-gap-2 tw-items-baseline tw-pb-2">
          <Title level={2} addClassName="tw-font-semibold">{LABEL_ASSIGN_ROLES}</Title>
          {optional && <Body addClassName="tw-text-gray-600" size="lg">{LABEL_OPTIONAL}</Body>}
        </div>
        {error && <Bold appearance="semibold" addClassName="tw-text-error">{error}</Bold>}
        <AssignRolesForm {...{ isLoading, onSubmit, member, tenant, optional }}>
          <Footer {...{ isEdit, onRemove, isLoading, member, tenant, onBackPressed, optional, length }}/>
        </AssignRolesForm>
      </ModalBody>
    </div>
  )
}

AssignRoles.propTypes = {}
export default AssignRoles
